import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FileText, Flag, SlidersHorizontal } from 'lucide-react'

import { CodifiedPlanRulesPane } from '../components/plan-algorithm/CodifiedPlanRulesPane'
import { MilestoneConfigPanel } from '../components/plan-algorithm/MilestoneConfigPanel'
import { PlanGenerationConfigPanel } from '../components/plan-algorithm/PlanGenerationConfigPanel'

type PlanAlgorithmTab = 'generation' | 'milestones' | 'rules'

const TAB_PARAM = 'tab'

const normalizeTab = (value: string | null): PlanAlgorithmTab => {
  if (value === 'milestones' || value === 'rules') {
    return value
  }

  return 'generation'
}

export const PlanAlgorithmPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = normalizeTab(searchParams.get(TAB_PARAM))
  const [mountedTabs, setMountedTabs] = useState<Set<PlanAlgorithmTab>>(() => new Set([activeTab]))
  const tabsToRender = new Set(mountedTabs).add(activeTab)

  const switchTab = (tab: PlanAlgorithmTab) => {
    setMountedTabs((current) => {
      const next = new Set(current)
      next.add(activeTab)
      next.add(tab)
      return next
    })
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        if (tab === 'generation') {
          next.delete(TAB_PARAM)
        } else {
          next.set(TAB_PARAM, tab)
        }
        return next
      },
      { replace: true }
    )
  }

  const tabButtonClass = (tab: PlanAlgorithmTab) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-primary text-primary'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Training Plans</p>
        <h1 className="text-3xl font-semibold text-foreground">Plan Algorithm</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Everything that shapes generated training plans lives here. The <strong>Generation Config</strong> tab
          holds the algorithm settings applied to all newly generated plans - every save keeps a restorable
          snapshot in Version history. The <strong>Milestone Schedule</strong> tab holds the singleton milestone
          table that is applied on top of the generated weeks.
        </p>
      </header>

      <div className="flex gap-2 border-b border-border">
        <button type="button" onClick={() => switchTab('generation')} className={tabButtonClass('generation')}>
          <SlidersHorizontal className="mr-2 inline-block h-4 w-4" />
          Generation Config
        </button>
        <button type="button" onClick={() => switchTab('milestones')} className={tabButtonClass('milestones')}>
          <Flag className="mr-2 inline-block h-4 w-4" />
          Milestone Schedule
        </button>
        <button type="button" onClick={() => switchTab('rules')} className={tabButtonClass('rules')}>
          <FileText className="mr-2 inline-block h-4 w-4" />
          Rules
        </button>
      </div>

      {tabsToRender.has('generation') && (
        <div className={activeTab === 'generation' ? 'space-y-4' : 'hidden'}>
          <PlanGenerationConfigPanel />
        </div>
      )}
      {tabsToRender.has('milestones') && (
        <div className={activeTab === 'milestones' ? 'space-y-4' : 'hidden'}>
          <MilestoneConfigPanel />
        </div>
      )}
      {tabsToRender.has('rules') && (
        <div className={activeTab === 'rules' ? 'space-y-4' : 'hidden'}>
          <CodifiedPlanRulesPane />
        </div>
      )}
    </div>
  )
}
